import { getCurrentUtilityId } from "../../guards/common/authentification-functions";
import { ngrokExtraHeaders } from "../../common/utils/serverHeaders";
import type { FeatureFlagClientSettings, TenantKey } from "../branding/types";

/**
 * Connection + identity details for the Flagsmith client SDK.
 *
 * The environment key is a *client-side* key: it is designed to be public and ships in
 * the bundle. It grants read-only access to resolved flag values for one environment.
 * The admin token is a different credential and must never reach the browser.
 *
 * Where the values come from, in order: the backend's branding response (Terraform owns
 * them there, see ai-docs/feature-flag-implementation-guide.md), then the build-time
 * variables below, then the local-stack default for the proxy. The build-time values are
 * what keeps local development and an older backend working, not the primary source.
 */

/** Build-time client-side key; the fallback when the backend sends none. */
export const FLAGSMITH_ENVIRONMENT_ID = process.env.REACT_APP_FLAGSMITH_ENVIRONMENT_ID ?? "";

/** Build-time Edge Proxy URL - never the core Flagsmith API. Defaults to the compose stack's proxy. */
export const FLAGSMITH_API_URL =
  process.env.REACT_APP_FLAGSMITH_API_URL ?? "http://localhost:8001/api/v1/";

/**
 * Build-time public blob container holding each utility's flag backup as `{url}/{utilityId}.json`,
 * with no trailing slash. Empty, with nothing from the backend either, turns the fallback off,
 * so a failed load reads as everything off, as it did before the backup existed.
 */
export const FEATURE_FLAG_BACKUP_URL = process.env.REACT_APP_FEATURE_FLAG_BACKUP_URL ?? "";

/** The three values a build carries, as one object - the fallback resolveFlagsmithSettings uses. */
export interface FlagsmithBuildSettings {
  environmentID: string;
  api: string;
  backupUrl: string;
}

export const BUILD_TIME_FLAGSMITH_SETTINGS: FlagsmithBuildSettings = {
  environmentID: FLAGSMITH_ENVIRONMENT_ID,
  api: FLAGSMITH_API_URL,
  backupUrl: FEATURE_FLAG_BACKUP_URL,
};

/** What the SDK is initialised with, and where the fallback reads from. */
export interface FlagsmithSettings extends FlagsmithBuildSettings {
  /**
   * Sent on every SDK request. Only non-empty when the proxy is reached through an ngrok
   * tunnel, whose free tier otherwise answers the browser with an HTML interstitial
   * instead of the flags - which the SDK reports as a failed load, i.e. everything off.
   */
  headers: Record<string, string>;
}

/**
 * Backend values first, build-time values second. A blank or whitespace backend value
 * counts as absent, so a backend that is configured for one field only (the local stack
 * sends the proxy URL but no key) never masks the build's value for the others.
 */
export function resolveFlagsmithSettings(
  fromBackend: FeatureFlagClientSettings | undefined,
  build: FlagsmithBuildSettings = BUILD_TIME_FLAGSMITH_SETTINGS,
): FlagsmithSettings {
  const pick = (backend: string | undefined, fallback: string) => backend?.trim() || fallback;
  const api = pick(fromBackend?.edgeProxyUrl, build.api);
  return {
    environmentID: pick(fromBackend?.clientSideKey, build.environmentID),
    api,
    backupUrl: pick(fromBackend?.backupUrl, build.backupUrl).replace(/\/+$/, ""),
    headers: ngrokExtraHeaders(api),
  };
}

/**
 * UtilityId per tenant, matching the `Utilities` array in each backend service's
 * appsettings.json. Only needed before login, where there is no JWT to read it from.
 *
 * TEMPORARY: this duplicates the backend registry, which is exactly the drift the
 * implementation guide flags as a prerequisite (`solar` is already missing from
 * TenantKey). Replace with the canonical registry once one exists.
 */
const UTILITY_IDS: Partial<Record<TenantKey, string>> = {
  xepp: "fafe2201-fd8e-407d-a6a4-5860d94a81af",
  aedc: "3187fa4d-97c5-408f-a85b-3f2bc54a87b8",
  lepp: "f39051be-2819-4c00-b41b-e2ee1e375e4a",
  ekedc: "23ec684b-4fe0-4ad8-b8e4-57528e53d34c",
  // `bps` is deliberately absent — see utilityIdForKey.
};

/**
 * The `utilityId` trait to target with, or `null` when there is no utility to target —
 * which means no flags at all, not "flags for some default tenant".
 *
 * `bps` is the BPS admin portal: a surface of its own, not a utility. It has no flags,
 * so it never reaches Flagsmith.
 *
 * Reading the JWT directly matters for timing. TenantProvider resolves `key` through an
 * async utility lookup, so on the first render after a refresh it is still the host/URL
 * fallback. Targeting on that would resolve another tenant's flags and briefly render
 * their features before correcting itself.
 *
 * Lower-cased because **Flagsmith's EQUAL operator is case-sensitive** and the segments
 * store the lower-case form that `Guid.ToString()` produces. A mismatch matches nothing
 * and every flag silently reads as its default.
 */
export function utilityIdForKey(key: TenantKey): string | null {
  if (key === "bps") return null;
  const id = getCurrentUtilityId() ?? UTILITY_IDS[key];
  return id ? id.toLowerCase() : null;
}

const DEVICE_ID_KEY = "xepp.flagsmith.deviceId";

/**
 * A stable, unique-per-browser id, generated once.
 *
 * Percentage rollouts bucket on a hash of this, so it must not be shared between users
 * — a per-tenant constant would put every anonymous user in the same bucket and turn a
 * 10% rollout into 0% or 100%.
 *
 * TODO: switch to the user id after login, so a user buckets consistently across
 * devices. Needs a user-id claim getter alongside getCurrentUtilityId().
 */
export function flagsmithIdentity(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}
